/**
 * @author        yewon@zumper.com (Yewon Lee)
 * @copyright     Copyright (c) 2017, Zumper
 * @description   RentableUtils
 */

import {
  External,
  LeaseType,
  PetType,
  PropertyFeatures,
  PropertyType,
  Zappable,
} from '@zcode/enums'
import { dollarize, isNumber } from '@zcode/utils'
import { format, isAfter, subMonths } from 'date-fns'
import * as CitiesC from '../../shared/constants/cities'
import { PRICE_PER_NIGHT_LEASE_DAYS, SHORT_TERM_LEASE_DAYS } from '../../shared/constants/filter'
import { HOT_PADS } from '../../shared/constants/source'
import { abbreviateNumber, discretize } from '../../shared/utils'
import { bedroomCountToBoolArray } from '../../shared/utils/filter'

export const NAtext = 'N/A'

/**
 *
 * @param {import('@zcode/state-type').Listable | import('@zcode/state-type').Property} item
 */
export const isBuilding = (item) => {
  return !!item.buildingId
}

/**
 *
 * @param {import('@zcode/state-type').Listable | import('@zcode/state-type').Property} item
 */
export const isListing = (item) => {
  return !!(!item.buildingId && item.listingId)
}

/**
 *
 * @param {import('@zcode/state-type').Rentable} item
 */
export const isRentSpecial = (item) => {
  return !!(item.features && item.features.includes(PropertyFeatures.SPECIALS))
}

// const isOpenListing = (item) => {
//   return item.listingType === ListingType.OPEN ||
//          item.listingType === ListingType.LIMITED
// }

/**
 *
 * @param {import('@zcode/state-type').Listable | import('@zcode/state-type').Property} item
 */
export const isExternal = (item) => {
  return item.external > 0
}

export const isNoDetailPage = (item) => {
  return item.external === External.NO_DETAIL
}

/**
 *
 * @param {import('@zcode/state-type').Listable | import('@zcode/state-type').Property} item
 */
export const isShortTerm = (item) => {
  const { leaseType, minLeaseDays } = item
  const leaseDaysShort = isNumber(minLeaseDays) && minLeaseDays < SHORT_TERM_LEASE_DAYS
  const isLeaseTypeShort =
    leaseType === LeaseType.MONTHLY ||
    leaseType === LeaseType.MONTHLY_X3 ||
    leaseType === LeaseType.MONTHLY_X6

  return leaseDaysShort || isLeaseTypeShort
}

/**
 *
 * @param {import('@zcode/state-type').Listable | import('@zcode/state-type').Property} item
 */
export const isViewable = (item) => {
  return (item.feedName !== HOT_PADS && !item.isInvalid) || !!item.isActive
}

/**
 *
 * @param {import('@zcode/state-type').Listable | import('@zcode/state-type').Property} item
 */
export const isSharedRoom = (item) => {
  return item.propertyType === PropertyType.SHARED_ROOM
}

/**
 *
 * @param {import('@zcode/state-type').Listable | import('@zcode/state-type').Property} item
 */
export const isZappable = (item) => {
  return item.zappable === Zappable.VERIFIED
}

/**
 *
 * @param {ReadonlyArray<PetType> | null | undefined} pets
 * @returns
 */
// something like these should be on the PetType.enum
export const checkCats = (pets) => {
  return !pets || !pets.length ? undefined : pets.includes(PetType.CAT)
}

/**
 * Returns true if either small or large dogs are allowed
 *
 * @param {ReadonlyArray<PetType> | null | undefined} pets
 * @returns
 */
export const checkDogs = (pets) => {
  return !pets || !pets.length
    ? undefined
    : pets.includes(PetType.SMALL_DOG) || pets.includes(PetType.LARGE_DOG)
}

/**
 *
 * @param {import('@zcode/state-type').Listable | import('@zcode/state-type').Property | import('@zcode/state-type').Pin} rentable
 * @returns {string}
 */
export const createListingMainHeader = (rentable) => {
  const { buildingName, isBuilding, name } = rentable
  let { address } = rentable
  address = String(address || '').replace('undefined', '') // sometimes "undefined" comes from the BE as string

  if (isBuilding) {
    return name || buildingName || address || ''
  }
  return address || ''
}

/**
 *
 * @param {import('@zcode/state-type').MaybeEmptyProperty | import('@zcode/state-type').Listable} rentable
 */
export const displayableArea = (rentable) => {
  const addressParts = []
  if (rentable.neighborhood) {
    addressParts.push(rentable.neighborhood.name)
    addressParts.push(rentable.neighborhood.city)
  } else if (rentable.neighborhoodName) {
    addressParts.push(rentable.neighborhoodName)
    addressParts.push(rentable.city)
  } else if (rentable.city) {
    addressParts.push(rentable.city)
    addressParts.push(rentable.state)
  }
  return addressParts.join(', ')
}

export const displayableAddress = (rentable) => {
  const addressParts = []
  if (rentable.address && typeof rentable.address === 'string') {
    addressParts.push(rentable.address.replace('undefined', '')) // sometimes address comes as "undefined" from the BE
  }

  if (rentable.neighborhood && rentable.neighborhood.city) {
    addressParts.push(rentable.neighborhood.city)
  }

  if (rentable.neighborhood && rentable.neighborhood.state) {
    addressParts.push(rentable.neighborhood.state)
  }

  return addressParts.join(', ')
}

export const fullDisplayableAddress = (rentable) => {
  const addressBase = displayableAddress(rentable)

  if (rentable.zipcode) {
    return `${addressBase} ${rentable.zipcode}`
  }

  return addressBase
}

export const formatBedroomText = (args = {}) => {
  const { minBeds, maxBeds, propertyType, full = false } = args
  if (propertyType === PropertyType.ROOM) {
    return 'Room'
  }
  if (propertyType === PropertyType.SHARED_ROOM) {
    return 'Shared Room'
  }

  let beds = full ? 'Bedroom' : 'Bed'
  beds = maxBeds <= 1 ? beds : `${beds}s`
  if (minBeds === maxBeds || maxBeds <= 0) {
    return minBeds === -1 ? 'Room' : minBeds === 0 ? 'Studio' : `${minBeds} ${beds}`
  }
  return `${minBeds === -1 ? 'Room' : minBeds === 0 ? 'Studio' : minBeds} - ${maxBeds} ${beds}`
}

export const getSimilarFilters = (rentable) => {
  const minb = rentable.minBedrooms || rentable.bedrooms || 0
  const minPrice = rentable.minPrice || rentable.price || undefined
  const maxPrice = rentable.maxPrice || rentable.price || undefined
  return {
    bedrooms: bedroomCountToBoolArray(minb),
    maxPrice: maxPrice ? discretize(maxPrice * 1.15, 100, 1) : undefined,
    minPrice: minPrice ? discretize(minPrice * 0.85, 100, -1) : undefined,
    // dogs: listable.dogsOk,
    // cats: listable.catsOk,
    // minBathrooms: listable.minBathrooms,
    // listingAmenities: listable.amenities,
    // buildingAmenities: listable.buildingAmenities,
  }
}

export const displaySqFt = (sqft, abbreviate) => {
  const regex = /\B(?=(\d{3})+(?!\d))/g
  return sqft ? sqft.toString().replace(regex, ',') + (abbreviate ? ' ft²' : ' SQFT') : ''
}

export const getNonRoomFloorplans = (floorplans) =>
  floorplans && floorplans.filter((floorplan) => floorplan.bedrooms >= 0)

export const minValue = (items, attr, defaultValue) => {
  if (!items) {
    return defaultValue
  }
  let value
  for (const item of items) {
    const val = item[attr]
    if (val !== undefined && val !== null) {
      value = value === undefined ? val : Math.min(val, value)
    }
  }
  return value === undefined ? defaultValue : value
}

export const maxValue = (items, attr, defaultValue) => {
  if (!items) {
    return defaultValue
  }
  let value
  for (const item of items) {
    const val = item[attr]
    if (val !== undefined && val !== null) {
      value = value === undefined ? val : Math.max(val, value)
    }
  }
  return value === undefined ? defaultValue : value
}

/**
 * parses the providerId-id
 *
 * @param {string} sourceId
 * @returns {string}
 */
export const parseProviderIdentifier = (sourceId) => {
  const x = (sourceId && sourceId.indexOf('.')) || 0
  return x > 0 ? sourceId.slice(1 + x) : null
}

/**
 * parses the providerId-source
 *
 * @param {string} sourceId
 * @returns {string}
 */
export const parseProviderSource = (sourceId) => {
  const x = (sourceId && sourceId.indexOf('.')) || 0
  return x > 0 ? sourceId.slice(0, x) : null
}

/**
 * parses the providerId-source
 *
 * @param {Date | undefined} date
 * @param {number} _now
 * @returns {string | undefined}
 */
export const getValidDateAvailable = (date, _now) => {
  let newDateString
  if (date) {
    const now = new Date(_now)
    if (isAfter(date, now)) {
      // Only show date if it's greater than today
      newDateString = format(date, 'MMMM do yyyy')
    } else if (isAfter(date, subMonths(now, 3))) {
      // Show 'Now' if was available in the past 3 months
      newDateString = 'Now'
    }
  }
  return newDateString
}

/**
 *
 * @param {import('@zcode/state-type').MaybeEmptyProperty} item
 */
export const getDisplayableLocationText = (item) => {
  if (!item) {
    return ''
  }

  const displayableAddressText = displayableAddress(item)
  const displayableAreaText = displayableArea(item)

  return (
    `${displayableAddressText || ''}` +
    `${displayableAddressText && displayableAreaText ? ', ' : ''}` +
    `${displayableAreaText || ''}`
  )
}

/**
 *
 * @param {*} param0
 * @returns {import('@zcode/state-type').Rentable['priceText']}
 */
export const getPriceText = ({ minPrice, maxPrice, withSpace }) => {
  // min - max if 0 < min < max
  // min if 0 < min = max
  // max if 0 = min < max
  // N/A if min = max = 0 or undefined
  if (minPrice && maxPrice) {
    const min = Math.min(minPrice, maxPrice)
    const max = Math.max(minPrice, maxPrice)
    if (min === max) {
      return dollarize(min)
    }
    return withSpace
      ? `${dollarize(min)} \u2013 ${dollarize(max)}`
      : `${dollarize(min)}\u2013${dollarize(max)}`
  }
  if (minPrice || maxPrice) {
    return dollarize(minPrice || maxPrice)
  }
  return NAtext
}

// Todo: move unit tests from rentable -> rentable_utils
/**
 *
 * @param {{ maxPrice: number | null; minPrice: number | null }} rentable
 * @returns {'N/A' | `$${number}${'' | 'B' | 'M' | 'K'}${'' | '+'}`}
 */
export const getPriceTxt = ({ minPrice, maxPrice }) => {
  // min+ if 0 < min < max
  // min if 0 < min = max
  // max if 0 = min < max
  // N/A if min = max = 0 or undefined
  if (minPrice && maxPrice) {
    const min = Math.min(minPrice, maxPrice)
    const max = Math.max(minPrice, maxPrice)
    const minTxt = min >= 1e4 ? `$${abbreviateNumber(min)}` : dollarize(min)
    if (min === max) {
      return minTxt
    }
    return `${minTxt}+`
  }
  if (minPrice || maxPrice) {
    const price = minPrice || maxPrice
    return price >= 1e4 ? `$${abbreviateNumber(price)}` : dollarize(price)
  }
  return NAtext
}

export const getExternalPriceTxt = ({ minPrice, maxPrice }) => {
  const priceTxt = getPriceTxt({ minPrice, maxPrice })
  if (priceTxt !== NAtext) {
    return `from ${priceTxt.replace('+', '')}`
  }
  return priceTxt
}

export const getPricePerNightText = ({ features, minLeaseDays, maxPrice, minPrice, price }) => {
  let listingPrice = price || minPrice
  const minLeaseDaysValidation = minLeaseDays && minLeaseDays < PRICE_PER_NIGHT_LEASE_DAYS
  const isExactPrice = minPrice === maxPrice

  if (!listingPrice || !minLeaseDaysValidation) {
    return ''
  }

  if (!features?.includes(PropertyFeatures.NIGHTLY_PRICING)) {
    listingPrice = Math.round(listingPrice / PRICE_PER_NIGHT_LEASE_DAYS)
  }

  return `${dollarize(listingPrice)}${isExactPrice ? '' : '+'}/night`
}

/**
 *
 * @param {Pick<import('@zcode/state-type').Listing, 'bathrooms' | 'halfBathrooms'>} listing
 */
export const getFullHalfBathroomTxt = ({ bathrooms, halfBathrooms }) => {
  if (!halfBathrooms) {
    return `${bathrooms} bath`
  }
  if (halfBathrooms === 1) {
    return `${bathrooms}.5 bath`
  }
  return `${bathrooms} full bath\n${halfBathrooms} half bath`
}
